import { Link } from "react-router-dom";

const IN_ARTICLE_IMG =
  "https://customer-assets.emergentagent.com/job_custom-team-jerseys-1/artifacts/l8dmj7l0_The%20Ultimate%20Polo%20Shirt%20Checklist.png";

export default function PacificFamilyReunionChecklist() {
  return (
    <>
      <p>
        Family reunions are more than just gatherings. They are an opportunity
        to reconnect with relatives, celebrate heritage, share stories, and
        create memories that will last for generations.
      </p>
      <p>
        One of the best ways to bring everyone together is through matching{" "}
        <Link to="/reunion-polo-shirts">reunion polo shirts</Link>. Not only
        do they create a sense of unity, but they also make family photos
        look professional and provide a keepsake relatives can treasure long
        after the event is over.
      </p>
      <p>
        If you're planning a Samoan, Tongan, Cook Islands, or wider Pacific
        family reunion, this checklist will help you organise the perfect
        custom polo shirt order — from confirming numbers and themes through
        to delivery and the all-important group photo.
      </p>

      <figure className="my-10">
        <img
          src={IN_ARTICLE_IMG}
          alt="Planning a Pacific Family Reunion — The Ultimate Polo Shirt Checklist by Tribal Designz, custom Samoan Tongan Cook Islands reunion polo uniforms NZ"
          loading="lazy"
          className="w-full border border-zinc-800"
        />
        <figcaption className="mt-3 text-xs text-zinc-500 uppercase tracking-wider">
          Tribal Designz · The Ultimate Pacific Family Reunion Polo Shirt
          Checklist
        </figcaption>
      </figure>

      <h2>Why matching polo shirts make family reunions better</h2>
      <p>
        Matching reunion shirts do more than coordinate the look of the day.
        They create:
      </p>
      <ul>
        <li>A sense of belonging across generations</li>
        <li>Easy identification during large events</li>
        <li>Professional family photographs that look intentional</li>
        <li>Lasting souvenirs your relatives will keep for years</li>
        <li>Stronger family pride that's visible the moment guests arrive</li>
      </ul>
      <p>
        Many Pacific reunions attract relatives from New Zealand, Australia,
        the United States, Samoa, Tonga and the Cook Islands. Matching{" "}
        <strong>Pacific family reunion polo shirts</strong> help everyone
        feel connected from the moment they step into the venue — no matter
        which country or generation they've travelled from.
      </p>

      <h2>Step 1: Confirm your guest numbers early</h2>
      <p>
        Before choosing a design, lock in the basics:
      </p>
      <ul>
        <li>How many adults will attend</li>
        <li>How many children (and what age range) will attend</li>
        <li>Whether you need men's, women's and youth sizing (XS – 7XL)</li>
        <li>Whether overseas relatives need shirts shipped ahead of the event</li>
      </ul>
      <p>
        Getting accurate numbers early can save you time, money, and the
        stress of last-minute size changes. Even a rough headcount lets us
        give you a more accurate quote.
      </p>

      <h2>Step 2: Choose a reunion theme</h2>
      <p>
        Your theme is what makes the design uniquely yours. Most families
        anchor the design around one or more of the following:
      </p>
      <ul>
        <li>Family surname (aiga / kainga / kopu tangata name)</li>
        <li>Village or island of origin</li>
        <li>Family crest or original-couple symbol</li>
        <li>Reunion year (often used as bold back-print)</li>
        <li>A short scripture verse or family motto</li>
      </ul>
      <p>Examples we've designed for:</p>
      <ul>
        <li>Leota Family Reunion 2026</li>
        <li>Manu'a Family Gathering</li>
        <li>Tofaeono Family Heritage Celebration</li>
        <li>Auckland Pacific Family Reunion</li>
      </ul>
      <p>A clear theme makes the design more meaningful — and the polo more meaningful to wear.</p>

      <h2>Step 3: Select colours that represent your family</h2>
      <p>
        Popular Pacific reunion colour combinations include:
      </p>
      <ul>
        <li><strong>Navy and white</strong> — classic, formal, very photogenic</li>
        <li><strong>Red, white and blue</strong> — traditional Samoan and Tongan palette</li>
        <li><strong>Black and gold</strong> — bold, premium feel</li>
        <li><strong>Green and white</strong> — popular for Cook Islands families</li>
        <li><strong>Maroon and cream</strong> — heritage feel with strong contrast</li>
      </ul>
      <p>
        Choose colours that reflect your family's heritage while ensuring
        the design remains easy to read from across a room (and across a
        group photo).
      </p>

      <h2>Step 4: Include Pacific-inspired design elements</h2>
      <p>
        This is where your reunion polo stops looking generic and starts
        looking like <em>yours</em>. Common cultural elements our reunion
        clients ask for include:
      </p>
      <ul>
        <li>
          <strong>Samoan motifs</strong> — siapo (tapa), tatau line work,
          village patterns. See our{" "}
          <Link to="/samoan-church-uniforms">Samoan church uniforms</Link>{" "}
          page for examples of the design language we use.
        </li>
        <li>
          <strong>Tongan motifs</strong> — ngatu (tapa) patterns, kupesi
          line work, kahoa (lei) illustrations. More on our{" "}
          <Link to="/tongan-church-uniforms">Tongan church uniforms</Link> page.
        </li>
        <li>
          <strong>Cook Islands motifs</strong> — tivaevae patterns, island
          crests, hibiscus. See{" "}
          <Link to="/cook-islands-uniforms">Cook Islands uniforms</Link>.
        </li>
        <li>Polynesian-inspired borders, geometric line work</li>
        <li>Family-specific cultural symbols approved by your elders</li>
      </ul>
      <p>
        These details transform a coordinated outfit into a family
        heirloom.
      </p>

      <h2>Step 5: Decide between t-shirts and polo shirts</h2>
      <p>
        While t-shirts are popular and affordable, polo shirts almost
        always provide:
      </p>
      <ul>
        <li>A more professional appearance for the family album</li>
        <li>Better family photographs (the collar lifts every shot)</li>
        <li>Greater comfort during church services, formal events and pō fiafia</li>
        <li>A premium keepsake relatives will continue wearing long after</li>
      </ul>
      <p>
        For larger family reunions and any event that crosses into church
        or formal settings, <strong>custom reunion polo shirts</strong> are
        the preferred choice.
      </p>

      <h2>Step 6: Collect sizes properly</h2>
      <p>
        This is where reunion organisers most often get caught out. Skip
        the multi-group-chat chaos and use a single sizing form. Include:
      </p>
      <ul>
        <li>Full name</li>
        <li>Adult or child sizing</li>
        <li>Size (with a clear size chart attached)</li>
        <li>Family branch or generation</li>
        <li>Contact number / email</li>
        <li>Payment status</li>
      </ul>
      <p>
        We can supply a printable and digital sizing template to help
        committees collect this info cleanly.
      </p>

      <h2>Step 7: Plan for group photos</h2>
      <p>
        One of the biggest payoffs of matching reunion polo shirts is the
        family photograph. Plan in advance for:
      </p>
      <ul>
        <li>Full whole-family photo (everyone present)</li>
        <li>Individual family branch photos</li>
        <li>Grandparents with grandchildren</li>
        <li>Cousins group photos</li>
        <li>Original-couple descendants by generation</li>
      </ul>
      <p>
        Matching apparel creates a professional and unified look that's
        worth printing, framing and gifting.
      </p>

      <h2>Step 8: Order early</h2>
      <p>
        For custom reunion uniforms, ordering early helps avoid:
      </p>
      <ul>
        <li>Production delays</li>
        <li>Shipping issues for overseas relatives</li>
        <li>Last-minute size changes</li>
        <li>Design-approval bottlenecks (committees take time)</li>
      </ul>
      <p>
        Ideally, place your order at least <strong>4 to 6 weeks</strong>{" "}
        before your reunion date. For very large multi-village reunions
        (200+ polos), plan 6–8 weeks ahead so we can comfortably split
        production and ship batches if needed.
      </p>

      <h2>Step 9: Create a lasting family keepsake</h2>
      <p>
        The best reunion shirts become more than clothing. Years later,
        family members will still wear them to:
      </p>
      <ul>
        <li>Church events and Sunday services</li>
        <li>Community gatherings, fa'alavelave and fundraisers</li>
        <li>School cultural days and sports days</li>
        <li>Future family reunions (as a "throwback" generation badge)</li>
      </ul>
      <p>
        A well-designed reunion polo becomes part of your family's story.
        That's the difference between getting shirts printed and{" "}
        <em>commissioning a family uniform</em>.
      </p>

      <h2>Final thoughts</h2>
      <p>
        Planning a Pacific family reunion takes organisation — but custom
        reunion polo shirts can make the event more memorable, more
        professional, and far more enjoyable for everyone involved.
        Whether you're organising a Samoan, Tongan, Cook Islands, Fijian
        or Niuean family gathering, matching polo shirts help strengthen
        family identity and create lasting memories that will be treasured
        for years to come.
      </p>

      <h3>Ready to create your reunion polo shirts?</h3>
      <p>
        Tribal Designz specialises in{" "}
        <Link to="/reunion-polo-shirts">Pacific-inspired reunion polo shirts</Link>{" "}
        for families throughout New Zealand. We're an Auckland-based,
        Pasifika-led studio that designs the polo with you, not for you.
      </p>
      <p>
        Want to see how your reunion could look?{" "}
        <Link to="/#contact">Request your free design mockup today</Link> —
        send a few details (family name, theme, rough quantity, deadline)
        and we'll send back a custom mockup and quote within 24 hours.
      </p>
    </>
  );
}
